import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import type { Extensions } from '@tiptap/core';

/**
 * `allowBase64: true` de propósito: sem Firebase Storage (plano Spark não deixa
 * habilitar Storage sem virar Blaze — decisão do usuário de ficar 100% de graça),
 * a imagem vira uma data URL direto no `src`, comprimida bem apertado no cliente
 * (ver `editor/imageUpload.ts`) pra caber no limite de ~1MiB por doc do Firestore
 * junto com o resto do post.
 */
export const BlogImage = Image.configure({
  allowBase64: true,
  HTMLAttributes: {
    class: 'rounded-2xl w-full h-auto my-6',
  },
});

/**
 * Heading com id estável (slug do texto, calculado em editor/headingOutline.ts
 * e injetado no JSON antes de renderizar — ver withHeadingIds) — sem isso o
 * `id` nunca apareceria no `<h1>`/`<h2>` gerado, porque generateHTML só
 * serializa atributos declarados no schema do nó. Usado pelo sumário lateral
 * do artigo (ArticleOutline) pra linkar/rolar até cada seção.
 */
const HeadingWithAnchor = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('id'),
        renderHTML: (attributes) => (attributes.id ? { id: attributes.id as string } : {}),
      },
    };
  },
});

/**
 * StarterKit v3 já inclui heading/blockquote/bulletList/orderedList/bold/
 * italic/link — cobre os 6 tipos de bloco + toda formatação da spec (seção 8)
 * sem precisar de extensões extras além da imagem.
 */
export function blockEditorExtensions(): Extensions {
  return [
    StarterKit.configure({
      heading: false, // substituído por HeadingWithAnchor abaixo (id p/ sumário)
      link: {
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: 'text-primary underline underline-offset-4' },
      },
    }),
    HeadingWithAnchor.configure({ levels: [1, 2] }), // Título (H1) / Subtítulo (H2) — spec não pede H3+
    BlogImage,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
      alignments: ['left', 'center', 'right', 'justify'],
    }),
  ];
}
